'use client';

import type { FC } from 'react';
import { Alert, Button, Card, Flex, Input, Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { GetKmsDocumentStatusResponse } from '~/app/services/kms/kms-document.types';
import {
  formatConfidencePercent,
  isAwaitingKmsExtraction,
} from '~/app/services/kms/kms-document.utils';
import { useKmsExtractedFieldsEditor } from '../../hooks/useKmsExtractedFieldsEditor';

const { Text } = Typography;
const { TextArea } = Input;

export interface KmsExtractedFieldsCardProps {
  document: GetKmsDocumentStatusResponse;
  organizationId: string | null;
  isSuperAdmin: boolean;
}

export const KmsExtractedFieldsCard: FC<KmsExtractedFieldsCardProps> = ({
  document,
  organizationId,
  isSuperAdmin,
}) => {
  const { t } = useTranslation('kms');
  const editor = useKmsExtractedFieldsEditor({
    document,
    organizationId,
    isSuperAdmin,
  });

  const awaitingExtraction = isAwaitingKmsExtraction(document);
  const waitingMessageKey = awaitingExtraction
    ? document.status === 'linked'
      ? 'detail.extractedFields.extracting'
      : 'detail.extractedFields.waiting'
    : 'detail.extractedFields.waiting';

  if (!editor.hasExtractedFields && document.status !== 'linked') {
    return null;
  }

  return (
    <Card
      className="kms-section-card"
      title={t('detail.extractedFields.title')}
      extra={
        editor.fieldsConfirmedAt ? (
          <Tag color="green">{t('detail.extractedFields.confirmed')}</Tag>
        ) : editor.hasExtractedFields ? (
          <Tag color="orange">{t('detail.extractedFields.pendingConfirm')}</Tag>
        ) : awaitingExtraction && document.status === 'linked' ? (
          <Tag color="blue">{t('detail.extractedFields.extracting')}</Tag>
        ) : (
          <Tag>{t('detail.extractedFields.waiting')}</Tag>
        )
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {document.status === 'write_failed' ? (
          <Alert
            type="error"
            showIcon
            message={t('detail.extractedFields.writeFailedTitle')}
            description={
              document.amsWriteError?.trim() ||
              t('detail.extractedFields.writeFailedUnknown')
            }
          />
        ) : null}

        <Text type="secondary">
          {t('detail.extractedFields.subtitle', {
            confidence: formatConfidencePercent(editor.extractionConfidence),
          })}
        </Text>

        {editor.flaggedFields.length > 0 ? (
          <Alert
            type="warning"
            showIcon
            message={t('detail.extractedFields.flaggedTitle')}
            description={editor.flaggedFields.join(', ')}
          />
        ) : null}

        {!editor.hasExtractedFields ? (
          <Alert type="info" showIcon message={t(waitingMessageKey)} />
        ) : (
          <TextArea
            value={editor.draftJson}
            onChange={(event) => editor.setDraftJson(event.target.value)}
            rows={16}
            disabled={!editor.canEdit || editor.isSaving || editor.isConfirming}
            style={{ fontFamily: 'monospace' }}
          />
        )}

        {editor.parseError ? (
          <Alert type="error" showIcon message={editor.parseError} />
        ) : null}

        {editor.canEdit ? (
          <Flex gap="small" wrap="wrap">
            <Button
              onClick={() => void editor.onSave()}
              loading={editor.isSaving}
              disabled={editor.isConfirming}
            >
              {t('detail.extractedFields.save')}
            </Button>
            <Button
              type="primary"
              onClick={() => void editor.onConfirm()}
              loading={editor.isConfirming}
              disabled={editor.isSaving}
            >
              {t('detail.extractedFields.confirm')}
            </Button>
          </Flex>
        ) : null}

        {editor.fieldsConfirmedAt ? (
          <Text type="secondary">
            {t('detail.extractedFields.confirmedAt', {
              date: new Date(editor.fieldsConfirmedAt).toLocaleString(),
            })}
          </Text>
        ) : null}
      </Space>
    </Card>
  );
};
